<script setup>
import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlEmptyState from '../../xl/XlEmptyState.vue';
import { hubEmptyState } from '../../../helpers/hubEmptyStates';
import { hubReader } from '../../../helpers/roles';
import { useAuthStore } from '../../../stores/auth';
import { compactDate, shortDate } from '../../../helpers/usDate';
import XlSkeleton from '../../xl/XlSkeleton.vue';
import XlStatusIcon from '../../xl/XlStatusIcon.vue';
import XlTable from '../../xl/XlTable.vue';
import XlTabs from '../../xl/XlTabs.vue';
import XlTag from '../../xl/XlTag.vue';
import XlTooltip from '../../xl/XlTooltip.vue';

const STATES = {
  ready_for_review: {
    label: 'Needs review',
    tone: 'warning',
    empty: 'Nothing needs review right now.',
    tip: 'The AI has drafted a transcript and recap. Nobody can see it until somebody reads it through and publishes it.',
  },
  processing: { label: 'Processing', tone: '', empty: 'Nothing is processing right now.' },
  approved: {
    label: 'Published',
    tone: 'success',
    empty: 'Nothing has been published yet.',
    tip: 'Policyholders can see this in their library right now.',
  },
  not_yet_recorded: { label: 'Not recorded yet', tone: '' },
  not_recorded: { label: 'Not recorded', tone: '' },
  // Somebody who spoke asked to be taken out after the session. Its own state
  // rather than a flavour of Published, because it overrides everything below
  // it: a held session reading "Published" would tell a host it is out there
  // when nobody can see it.
  held: {
    label: 'On hold',
    tone: 'warning',
    empty: 'Nothing is on hold.',
    tip: 'A speaker withdrew. Members cannot see the recording, transcript or recap until you clear this.',
  },
};
const FILTERS = ['ready_for_review', 'processing', 'approved'];
const RECORDED_TIP = 'This session will be recorded, and you\'ll be able to watch it back afterwards.';

const router = useRouter();
const authStore = useAuthStore();
const emptyState = computed(() => hubEmptyState('recordings', {
  reader: hubReader(authStore),
  role: authStore.userRole,
  capabilities: authStore.capabilities,
  empty: true,
}));
const loading = ref(true);
const error = ref('');
const sessions = ref([]);
const filter = ref('all');

const withStatus = (status) => sessions.value.filter((s) => s.status === status);

// While loading, a figure space holds each count pill's place (Policyholders' tabs do the same).
const countOf = (n) => (loading.value ? '\u2007' : n);
const tabs = computed(() => [
  { id: 'all', label: 'All', count: countOf(sessions.value.length) },
  // A hold is rare and always temporary, so its tab appears only when there
  // is one. A permanent "On hold 0" would be a tab that is almost never about
  // anything, next to three that always are.
  ...(withStatus('held').length ? ['ready_for_review', 'held'] : ['ready_for_review'])
    .concat(['processing', 'approved'])
    .map((status) => ({ id: status, label: STATES[status].label, count: countOf(withStatus(status).length) })),
]);
const shown = computed(() => (filter.value === 'all' ? sessions.value : withStatus(filter.value)));
const waiting = computed(() => withStatus('ready_for_review'));

// Whether there were recordings waiting last time, for this session: the
// banner's place is then held while the list loads, rather than the banner
// arriving with it and pushing the tabs and table down ("Recordings jumps").
const WAITING_KEY = 'recordings:waiting';
const hadWaiting = (() => {
  try {
    return sessionStorage.getItem(WAITING_KEY) === '1';
  } catch {
    return false;
  }
})();
watch(() => (loading.value || error.value ? null : waiting.value.length), (count) => {
  if (count === null) return;
  try {
    sessionStorage.setItem(WAITING_KEY, count ? '1' : '0');
  } catch {
    // A per-tab convenience: nothing breaks without it.
  }
});

const canOpen = (session) => !session.status.startsWith('not_');

const open = (session) => {
  if (!canOpen(session)) return;
  router.push({ name: 'Recording Detail', params: { agendaEventId: session.id } });
};

const reviewWaiting = () => {
  if (waiting.value.length === 1) open(waiting.value[0]);
  else filter.value = 'ready_for_review';
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const formatRecorded = (session) => {
  const d = new Date(session.start_date);
  if (Number.isNaN(d.getTime())) return '';
  const date = new Date() - d < WEEK_MS ? compactDate(d) : shortDate(d);
  return canOpen(session) ? `Recorded ${date}` : date;
};

onMounted(async () => {
  try {
    const { data } = await axios.get('/api/recording-transcription/sessions');
    sessions.value = data?.data ?? [];
  } catch (e) {
    error.value = e?.response?.data?.error ?? e.message;
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="recordings" data-xl-gen="northstar">
    <div class="recordings__page">
      <header class="recordings__head">
        <h1>Recording &amp; transcription</h1>
        <p>Every recorded session lands here as a draft. Nobody sees it until somebody reads it through and publishes it.</p>
      </header>

      <!--
        The page as it will be — the tabs, the table's own head, and rows of
        placeholders in its columns at a real row's height (Recordings: four
        lines of text stood in for a table, and everything jumped when it came).
      -->
      <template v-if="loading">
        <XlSkeleton v-if="hadWaiting" height="88px" radius="var(--xl-radius-lg, 20px)" class="recordings__waiting-sk" data-testid="waiting-banner-loading" />
        <XlTabs :model-value="filter" :tabs="tabs" />
        <XlCard class="recordings__table" aria-busy="true" data-testid="recordings-loading">
          <XlTable>
            <template #head>
              <th>Session</th>
              <th>Recorded</th>
              <th>Length</th>
              <th>Parts</th>
              <th>Status</th>
              <th>Views</th>
              <th>Host</th>
              <th />
            </template>
            <tr v-for="w in ['52%', '40%', '61%', '47%', '56%']" :key="w" data-testid="recordings-row-loading">
              <td class="recordings__title"><XlSkeleton :width="w" height="0.9em" radius="6px" class="recordings__sk" /></td>
              <td><XlSkeleton width="96px" height="0.85em" radius="6px" class="recordings__sk" /></td>
              <td><XlSkeleton width="44px" height="0.85em" radius="6px" class="recordings__sk" /></td>
              <td><XlSkeleton width="16px" height="0.85em" radius="6px" class="recordings__sk" /></td>
              <td><XlSkeleton width="112px" height="24px" radius="99px" class="recordings__sk" /></td>
              <td><XlSkeleton width="24px" height="0.85em" radius="6px" class="recordings__sk" /></td>
              <td><XlSkeleton width="92px" height="0.85em" radius="6px" class="recordings__sk" /></td>
              <td class="recordings__chev" />
            </tr>
          </XlTable>
        </XlCard>
      </template>

      <XlCard v-else-if="error" padded tone="error" role="alert">{{ error }}</XlCard>

      <XlEmptyState
        v-else-if="!sessions.length && emptyState"
        variant="page"
        :icon="emptyState.icon"
        :headline="emptyState.headline"
        :body="emptyState.body"
        :actions="emptyState.actions"
        data-testid="recordings-empty"
        @action="router.push({ name: 'Create Session' })"
      />

      <template v-else>
        <XlCard v-if="waiting.length" padded tone="warning" class="recordings__waiting" data-testid="waiting-banner">
          <XlStatusIcon tone="warning">
            <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4.4 3.6 19.6h16.8z" /><path d="M12 10v3.8M12 16.8h.01" /></svg>
          </XlStatusIcon>
          <div class="recordings__waiting-text">
            <b>{{ waiting.length === 1 ? 'One recording is waiting for you' : `${waiting.length} recordings are waiting for you` }}</b>
            <span>The AI has done a first draft of the transcript and the recap. Read it through before policyholders see it.</span>
          </div>
          <XlButton @click="reviewWaiting">{{ waiting.length === 1 ? 'Review it now' : 'Show them' }}</XlButton>
        </XlCard>

        <XlTabs v-model="filter" :tabs="tabs" />

        <XlCard v-if="!shown.length" padded class="recordings__empty">{{ STATES[filter].empty }}</XlCard>

        <XlCard v-else class="recordings__table">
          <XlTable>
            <template #head>
              <th>Session</th>
              <th><XlTooltip underline :text="RECORDED_TIP" class="recordings__th-tip">Recorded</XlTooltip></th>
              <th>Length</th>
              <th>Parts</th>
              <th>Status</th>
              <th>Views</th>
              <th>Host</th>
              <th />
            </template>
            <tr
              v-for="session in shown"
              :key="session.id"
              :class="{ 'is-clickable': canOpen(session) }"
              @click="open(session)"
            >
              <td class="recordings__title">
                <a
                  v-if="canOpen(session)"
                  :href="`/recordings/${session.id}`"
                  @click.stop.prevent="open(session)"
                >{{ session.title }}</a>
                <template v-else>{{ session.title }}</template>
              </td>
              <td class="recordings__muted">{{ formatRecorded(session) }}</td>
              <td :class="{ recordings__faint: session.duration_label === '—' }">{{ session.duration_label }}</td>
              <td :class="session.recording_count ? 'recordings__muted' : 'recordings__faint'">{{ session.recording_count || '—' }}</td>
              <td>
                <XlTooltip v-if="STATES[session.status]?.tip" :text="STATES[session.status].tip" class="recordings__tip">
                  <XlTag :tone="STATES[session.status].tone">{{ STATES[session.status].label }}</XlTag>
                </XlTooltip>
                <XlTag v-else :tone="STATES[session.status]?.tone ?? ''">{{ STATES[session.status]?.label ?? session.status }}</XlTag>
              </td>
              <td :class="{ recordings__faint: !session.views }">{{ session.views || '—' }}</td>
              <td :class="session.host ? 'recordings__muted' : 'recordings__faint'">{{ session.host || '—' }}</td>
              <td class="recordings__chev">
                <svg v-if="canOpen(session)" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
              </td>
            </tr>
          </XlTable>
        </XlCard>
      </template>
    </div>
  </div>
</template>

<style scoped>
.recordings { color: var(--xl-color-primary-text); }

.recordings__page {
  box-sizing: border-box;
  padding: var(--setup-page-padding) var(--setup-page-padding) 70px;
}

@media (max-width: 600px) {
  .recordings__page { padding: 18px 16px 60px; }
}

.recordings__head { margin-bottom: 22px; }

.recordings__sk { width: auto; vertical-align: middle; }

.recordings__head h1 {
  margin: 0 0 6px;
  font-size: 30px;
  font-weight: var(--xl-font-weight-bold);
  letter-spacing: -0.03em;
}

.recordings__head p {
  margin: 0;
  font-size: 15px;
  color: var(--xl-color-outline);
}

.recordings__waiting-sk { display: block; margin-bottom: 18px; }

.recordings__waiting {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 13px;
  margin-bottom: 18px;
}

.recordings__waiting-text {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.recordings__waiting-text b { font-size: 15px; }

.recordings__waiting-text span {
  font-size: 13.5px;
  color: var(--xl-color-secondary-text);
}

.recordings__empty {
  font-size: 14px;
  color: var(--xl-color-secondary-text);
}

.recordings__table { overflow: hidden; }

.recordings__table :deep(th) { height: 46px; }

.recordings__table :deep(td) {
  font-size: 14px;
  border-bottom-color: var(--xl-color-border-subtle);
}

.recordings__title { font-weight: 700; }

.recordings__tip { cursor: help; }

/* Spans XlTable's th padding, so the whole header cell is the trigger and the
   tip anchors to the cell, as the prototype's does. */
.recordings__table .recordings__th-tip {
  display: flex;
  margin: -10px -16px;
  padding: 10px 16px;
  text-decoration-color: var(--xl-color-outline-variant);
  text-decoration-thickness: 1px;
  outline-offset: -2px;
}

.recordings__title a {
  color: inherit;
  text-decoration: none;
}

.recordings__title a:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: 3px;
  border-radius: 4px;
}

/* XlTable inks every cell with `.xl-table td`, which a lone class loses to. */
.recordings__table td.recordings__muted { color: var(--xl-color-outline); }

.recordings__table td.recordings__faint { color: var(--xl-color-outline-variant); }

.recordings__chev { width: 32px; }

.recordings__chev svg { display: block; }
</style>
